import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { BillingAddress, BillingAddressSelection } from '../../models/billing-address.model';
import { BillingAddressService } from '../../services/billing-address.service';
import { ToastService } from '../../services/notification/toast.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { WebService } from '../../services/web/web.service';
import { billingAddressErrorMessage, isPositiveIntegerId } from '../../shared/utils/billing-address-http.util';
import { formatAddressDetail, formatCardExpiration, maskCardNumber } from '../../shared/utils/card-display.util';

export interface CardInfo {
  id: number;
  maskCreditCard: string;
  provider: string;
  holder: string;
  expiration: string;
  billingAddress: BillingAddress | null;
}

interface LoadResult {
  card: CardInfo | null;
  addresses: BillingAddress[];
}

@Component({
  selector: 'app-update-card',
  templateUrl: './update-card.component.html',
  styleUrls: ['./update-card.component.scss'],
  standalone: false
})
export class UpdateCardComponent implements OnInit {
  customerId = '';
  cardId: number | null = null;
  card: CardInfo | null = null;
  addresses: BillingAddress[] = [];
  selection: BillingAddressSelection | null = null;
  showPicker = false;
  saving = false;
  /** Formulario inline de nueva dirección abierto o enviando (bloquea Guardar). */
  addressFormBusy = false;
  loaded = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private webService: WebService,
    private billingAddressService: BillingAddressService,
    private destroyRef: DestroyRef,
    private spinner: SpinnerService,
    private toast: ToastService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const customerId = this.activatedRoute.snapshot.paramMap.get('id');
    const cardId = this.activatedRoute.snapshot.paramMap.get('cardId');
    this.customerId = isPositiveIntegerId(customerId) ? customerId : '';
    if (!this.customerId || !isPositiveIntegerId(cardId)) {
      this.leaveWithError('customerCards.cardNotFound');
      return;
    }
    this.cardId = Number(cardId);
    this.load();
  }

  get currentAddress(): BillingAddress | null {
    return this.card?.billingAddress ?? null;
  }

  get currentAddressId(): number | null {
    return this.currentAddress?.id ?? null;
  }

  /** Hay selección y es distinta de la dirección actual de la tarjeta. */
  get hasChange(): boolean {
    if (!this.selection) {
      return false;
    }
    return this.selection.addressId !== this.currentAddressId;
  }

  get canSave(): boolean {
    return this.hasChange && !this.saving && !this.addressFormBusy;
  }

  /** Calle de la dirección seleccionada. */
  get selectedStreet(): string {
    const selected = this.selection;
    return selected ? this.addresses.find(a => a.id === selected.addressId)?.address ?? '' : '';
  }

  /** Segunda línea ("Ciudad, PAÍS CP") de la dirección actual de la tarjeta. */
  get currentAddressDetail(): string {
    return this.currentAddress ? formatAddressDetail(this.currentAddress) : '';
  }

  onSelectionChange(selection: BillingAddressSelection | null): void {
    this.selection = selection;
  }

  onBusyChange(busy: boolean): void {
    this.addressFormBusy = busy;
  }

  /** Dirección creada desde el picker: se agrega a la lista si aún no está. */
  onAddressCreated(created: BillingAddress): void {
    if (!this.addresses.some(a => a.id === created.id)) {
      this.addresses = [...this.addresses, created];
    }
  }

  openPicker(): void {
    this.showPicker = true;
  }

  cancel(): void {
    this.goToCards();
  }

  save(): void {
    const selection = this.selection;
    if (!selection || !this.canSave || this.cardId === null) {
      return;
    }
    this.saving = true;
    const lease = this.spinner.acquire();
    this.billingAddressService.setCardBillingAddress(this.cardId, selection.addressId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.saving = false;
        lease.release();
      })
    ).subscribe({
      next: () => {
        this.toast.showSuccess(this.translate.instant('customerCards.addressSaved'));
        this.goToCards();
      },
      error: err => this.toast.showError(this.errorMessage(err))
    });
  }

  errorMessage(error: unknown): string {
    return billingAddressErrorMessage(error, this.translate);
  }

  private load(): void {
    const lease = this.spinner.acquire();
    const addresses$ = this.billingAddressService.list(this.customerId)
      .pipe(catchError(() => of([] as BillingAddress[])));
    forkJoin({ card: this.loadCard(), addresses: addresses$ }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => lease.release())
    ).subscribe({
      next: (result: LoadResult) => {
        if (!result.card) {
          this.leaveWithError('customerCards.cardNotFound');
          return;
        }
        this.card = result.card;
        this.addresses = this.withCurrentAddress(result.addresses, result.card.billingAddress);
        this.showPicker = !result.card.billingAddress;
        this.loaded = true;
      },
      error: () => {
        this.leaveWithError('customerCards.errorLoadingCards');
      }
    });
  }

  private leaveWithError(messageKey: string): void {
    this.toast.showError(this.translate.instant(messageKey));
    this.goToCards();
  }

  /** La dirección actual siempre debe estar en la lista del picker (aunque el listado falle o no la incluya). */
  private withCurrentAddress(list: BillingAddress[], current: BillingAddress | null): BillingAddress[] {
    return current && !list.some(a => a.id === current.id) ? [current, ...list] : list;
  }

  private loadCard(): Observable<CardInfo | null> {
    return this.webService
      .get(`${this.webService.HOST}/creditcard/customer/${encodeURIComponent(this.customerId)}?excludePartnerGeo=true`)
      .pipe(
        map(res => ((res?.result ?? []) as any[]).find(c => Number(c.id) === this.cardId) ?? null),
        switchMap(raw => (raw ? this.toCardInfo(raw) : of(null)))
      );
  }

  /** El titular no siempre viene en el listado: se completa desde customerHasCreditCard (como el listado de tarjetas). */
  private toCardInfo(raw: any): Observable<CardInfo> {
    const holder$: Observable<string> = raw.nameHolderCreditCard || raw.corerId == null
      ? of(raw.nameHolderCreditCard ?? '')
      : this.webService
        .get(`${this.webService.HOST}/customer/${encodeURIComponent(this.customerId)}/customerHasCreditCard/${encodeURIComponent(String(raw.corerId))}`)
        .pipe(
          map(res => res?.result?.creditCard?.nameHolderCreditCard ?? ''),
          catchError(() => of(''))
        );
    return holder$.pipe(map(holder => ({
      id: Number(raw.id),
      maskCreditCard: maskCardNumber(raw.mask_card),
      provider: raw.creditCardProvider?.description ?? '',
      holder,
      expiration: formatCardExpiration(raw.expiration_month, raw.expiration_year),
      billingAddress: raw.billingAddress ?? null
    })));
  }

  private goToCards(): void {
    this.router.navigate(this.customerId ? ['reports', this.customerId, 'cards'] : ['reports']);
  }
}
